import React, { useState, useEffect, useCallback } from 'react';
import { Page } from '../../layout';
import AppraisalComparisonFilters from './AppraisalComparisonFilters';
import AppraisalCriteriaSection from './AppraisalCriteriaSection';
import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { userPrivileges, userTier } from '../../../apollo/cache';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import GET_APPRAISAL_COMPARISON_ORG_UNIT from '../queries/getAppraisalComparisonOrgUnitQuery';
import UnitComparisonRadar from './UnitComparisonRadar';
import { selectedPracticeArea } from '../appraisalComparisonCache';
import PracticeCharacterizationSection from './PracticeCharacterizationSection';
import AggregatePercentageSection from './AggregatePercentageSection';
import GET_ORG_REPORT from '../queries/getOrgAppraisalReport';
import GET_INDUSTRY_APPRAISAL_VIEW from '../queries/getIndustryAppraisalViewReport';
import _, { get } from 'lodash';
import UnitDetailsBox from '../../shared/components/UnitDetailsBox';
import {
  APPRAISAL_SPONSOR,
  APPRAISAL_TEAM_LEAD
} from '../../../constants/appraisal-team-member-types';
import { isUserFDATier } from '../../shared/services/checkUserTierService';
import DownloadAppraisalReportModal from './DownloadAppraisalReportModal';
import {
  APPRAISAL_COMPARISON_FILTER_TYPES,
  FILTER_TYPES_KEYS
} from '../../../constants/appraisal-comparison-filter-types';
import PageInstructions from '../../shared/components/PageInstructions';
import PrivilegeNames from '../../../constants/privilegeNames';
import { getUnitSearchForAppraisalComparison } from '../../shared/services/getUnitSearchPageUrlService';
import GET_AGGREGATE_PERCENTAGE_DATA from '../queries/getAggregatePercentageDataQuery';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import {
  canUserAccessOrgUnitName,
  canUserAccessSeveralOrganization
} from '../../shared/services/userAccessibilityService';
import { getPageTitle } from '../../shared/services/pageTitleService';
import { toHumanReadableDate } from '../../shared/services/dateService';
import useOrgUnitIdFromURL from '../../shared/hooks/useOrgUnitIdFromURL';
import ApolloError from '../../shared/components/ApolloError';
import { getDataNotFoundPage } from '../../shared/services/getDataNotFoundPageService';
import OrgUnitBreadcrumbAnnotation from '../../shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';
import AppraisalComparisonMapper from './shared/mapper/AppraisalComparisonMapper';

const AppraisalComparison = () => {
  const orgUnitId = useOrgUnitIdFromURL();

  const location = useLocation();
  const orgUnitSelectedAppraisalId = location.state
    ? location.state.orgUnitSelectedAppraisalId
    : null;

  const privileges = useReactiveVar(userPrivileges);
  const selectedPracticeAreaId = useReactiveVar(selectedPracticeArea);
  const tier = useReactiveVar(userTier);

  const [appraisalViewRequest, setAppraisalViewRequest] = useState(null);
  const [unitAppraisalData, setUnitAppraisalData] = useState([]);
  const [practiceArea, setPracticeArea] = useState('');
  const [practiceNameAndPercentageDataUnitDetails, setPracticeNameAndPercentageDataUnitDetails] =
    useState(defaultPracticeNameAndPercentageDataUnitDetails);
  const [unitDetailsBoxData, setUnitDetailsBoxData] = useState({});
  const [isProgramYearSharedWithFDA, setIsProgramYearSharedWithFDA] = useState(false);
  const [
    showPracticeCharacterizationUnitForFdaUser,
    setShowPracticeCharacterizationUnitForFdaUser
  ] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedAppraisalId, setSelectedAppraisalId] = useState(orgUnitSelectedAppraisalId);
  const [comparisonType, setComparisonType] = useState(APPRAISAL_COMPARISON_FILTER_TYPES[0]);
  const [selectedDataRange, setSelectedDataRange] = useState(null);

  const isUserFDA = isUserFDATier(tier);
  const previousLink =
    !privileges.includes(PrivilegeNames.UnitSearch) &&
    !privileges.includes(PrivilegeNames.UnitSelection)
      ? '/data-reports/industry-reports'
      : getUnitSearchForAppraisalComparison(privileges);

  const navigate = useNavigate();

  const {
    data: orgReportData,
    loading: orgReportLoading,
    error: orgReportError
  } = useQuery(GET_ORG_REPORT, {
    variables: {
      orgUnitId: orgUnitId,
      appraisalViewRequest: appraisalViewRequest
    },
    skip: !appraisalViewRequest
  });

  const {
    data: industryReportData,
    loading: industryReportLoading,
    error: industryReportError
  } = useQuery(GET_INDUSTRY_APPRAISAL_VIEW, {
    variables: {
      appraisalViewRequest: appraisalViewRequest
    },
    skip: !appraisalViewRequest
  });

  const {
    data,
    error,
    loading: appraisalComparisonLoading
  } = useQuery(GET_APPRAISAL_COMPARISON_ORG_UNIT, {
    variables: {
      id: orgUnitId
    }
  });

  const orgUnitName =
    data && data.appraisalComparisonOrgUnit && data.appraisalComparisonOrgUnit.Name;
  const orgName =
    data &&
    data.appraisalComparisonOrgUnit &&
    data.appraisalComparisonOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = data && (
    <OrgUnitBreadcrumbAnnotation orgUnit={data.appraisalComparisonOrgUnit} key={orgUnitName} />
  );

  const pageTitle = getPageTitle([
    canUserAccessOrgUnitName(tier) ? orgUnitName : '',
    'Appraisal Comparison'
  ]);
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, renderedOrgUnit]
    : [renderedOrgUnit];
  const breadcrumbPathItems = [
    { name: 'Data Reports', url: '/data-reports' },
    { name: 'Industry Reports', url: previousLink },
    { name: 'Appraisal Comparison', url: '' }
  ];

  const [searchAggregatePercentage, { error: searchAggregatePercentageError }] = useLazyQuery(
    GET_AGGREGATE_PERCENTAGE_DATA,
    {
      onCompleted: result => {
        setPracticeNameAndPercentageDataUnitDetails(
          <span className='margin-top'>
            <strong>{selectedDataRange + ': '}</strong>
            {getAppraisalUnits(result.aggregatePercentageData)}
          </span>
        );
      }
    }
  );

  const setAppraisalOrgUnitInfo = useCallback(
    appraisalOrgUnit => {
      if (!appraisalOrgUnit) {
        return;
      }
      const appraisalOrgUnitPracticeAreas = appraisalOrgUnit.AppraisalOrgUnitTargetPracticeAreas;

      if (isUserFDA) {
        setShowPracticeCharacterizationUnitForFdaUser(
          appraisalOrgUnit.ShowPracticeCharacterizationForUnit
        );
      }
      if (!isUserFDA) {
        setIsProgramYearSharedWithFDA(appraisalOrgUnit.IsSharedWithFda);
      }
      setUnitAppraisalData(appraisalOrgUnitPracticeAreas);
      setSelectedYear(appraisalOrgUnit.AppraisalProgramYear);

      const appraisalViewRequests = AppraisalComparisonMapper.toAppraisalViewRequests(
        appraisalOrgUnitPracticeAreas,
        appraisalOrgUnit.AppraisalProgramYear
      );
      setAppraisalViewRequest(appraisalViewRequests);
    },
    [isUserFDA]
  );

  useEffect(() => {
    setAppraisalViewRequest(null);
    setSelectedAppraisalId(orgUnitSelectedAppraisalId);
  }, [orgUnitId, orgUnitSelectedAppraisalId]);

  useEffect(() => {
    if (!data || !data.appraisalComparisonOrgUnit) {
      return;
    }

    if (!selectedAppraisalId) {
      const appraisalOrgUnitMaxProgramYear = _.maxBy(
        data.appraisalComparisonOrgUnit.AppraisalOrgUnits,
        unit => unit.AppraisalProgramYear
      );
      setSelectedAppraisalId(appraisalOrgUnitMaxProgramYear.Appraisal.Id);
      return;
    }

    setPracticeNameAndPercentageDataUnitDetails(defaultPracticeNameAndPercentageDataUnitDetails);

    const mddapOrgUnit = data.appraisalComparisonOrgUnit;
    const appraisalOrgUnits = mddapOrgUnit.AppraisalOrgUnits;
    const appraisalOrgUnit = appraisalOrgUnits.find(a => a.Appraisal.Id === selectedAppraisalId);

    setAppraisalOrgUnitInfo(appraisalOrgUnit);

    const unitInfo = getUnitInfoForMoreInfoBox(mddapOrgUnit);
    const appraisalInfo = getAppraisalInfoForMoreInfoBox(appraisalOrgUnit);
    setUnitDetailsBoxData({ ...unitInfo, ...appraisalInfo });
  }, [data, selectedAppraisalId, setAppraisalOrgUnitInfo]);

  useEffect(() => {
    if (!unitAppraisalData || !selectedPracticeAreaId) {
      return;
    }
    const selectedPa = unitAppraisalData.filter(x => x.PracticeArea.Id == selectedPracticeAreaId);
    if (selectedPa && selectedPa.length > 0) {
      setPracticeArea(`${selectedPa[0].PracticeArea.Name} (${selectedPa[0].PracticeArea.Acronym})`);
    }
  }, [unitAppraisalData, selectedPracticeAreaId]);

  const handlePracticeClick = data => {
    if (!data) {
      return;
    }

    setPracticeNameAndPercentageDataUnitDetails(
      <p>
        <strong>{data.Acronym + ' ' + data.Level + '.' + data.NumberInLevel + ': '}</strong>
        {data.PracticeName}
      </p>
    );
  };

  const handleAggregatePercentageClick = async (data, clickedBarDataKey) => {
    const aggregatePercentageRequest = appraisalViewRequest.find(x => x.practiceAreaId == data.Id);
    setSelectedDataRange(data.range);

    searchAggregatePercentage({
      variables: {
        ...aggregatePercentageRequest,
        orgUnitId: orgUnitId,
        range: data.range,
        isIndustry: clickedBarDataKey == FILTER_TYPES_KEYS.Industry
      }
    });
  };

  const handleSelectUnit = orgUnitId => {
    navigate(`/data-reports/industry-reports/appraisal-comparison/${orgUnitId}`, {
      state: {
        orgUnitSelectedAppraisalId: selectedAppraisalId
      }
    });
  };

  const getAppraisalUnits = aggregateDataMddapOrgUnits => {
    if (!aggregateDataMddapOrgUnits || !aggregateDataMddapOrgUnits.length) {
      return 'N/A';
    }

    return (
      <ul className='no-margin'>
        {aggregateDataMddapOrgUnits.map((orgUnits, index) => {
          return (
            <li key={`${orgUnits.MddapOrgUnitName}-${index}`}>
              {orgUnits.IsUserHasAccessOrgUnit ? (
                <Link
                  to={`/data-reports/industry-reports/appraisal-comparison/${orgUnits.MddapOrgUnitId}`}
                  state={{ orgUnitSelectedAppraisalId: orgUnits.AppraisalId }}>
                  {orgUnits.MddapOrgUnitName}
                </Link>
              ) : (
                orgUnits.MddapOrgUnitName
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const handleClickOutside = () => {
    setPracticeNameAndPercentageDataUnitDetails(defaultPracticeNameAndPercentageDataUnitDetails);
  };

  const getUnitInfoForMoreInfoBox = orgUnit => {
    const appraisalOrgUnits = get(orgUnit, 'AppraisalOrgUnits', []);
    const latestAppraisal = appraisalOrgUnits[appraisalOrgUnits.length - 1].Appraisal;
    const address = [
      orgUnit.Address1,
      orgUnit.Address2,
      orgUnit.City,
      orgUnit.State,
      orgUnit.ZipCode
    ]
      .filter(Boolean)
      .join(', ');

    const OHTs = orgUnit.MddapOfficeOfHealthTechnologies.map(x =>
      x.OfficeOfHealthTechnology.Value.split(':')[0].replace('OHT', '')
    ).join(', ');
    const enrollmentDate = toHumanReadableDate(orgUnit.EnrollmentDate);
    const sponsorName = get(latestAppraisal, 'AppraisalTeamMembers', []).find(
      x => x.Type === APPRAISAL_SPONSOR
    ).Individual.Name;

    return {
      location: address,
      FEI: orgUnit.Fei,
      enrollmentDate: enrollmentDate,
      OHTs: OHTs,
      sponsorName: sponsorName
    };
  };

  const getAppraisalInfoForMoreInfoBox = appraisalOrgUnit => {
    if (!appraisalOrgUnit) {
      return;
    }
    const appraisal = appraisalOrgUnit.Appraisal;
    return {
      appraisalDate: toHumanReadableDate(appraisal.ConductAppraisalEndDate),
      appraisalID: appraisal.Id,
      teamLeadName: appraisal.AppraisalTeamMembers.find(x => x.Type === APPRAISAL_TEAM_LEAD)
        .Individual.Name,
      appraisalUnits: appraisal.AppraisalOrgUnits
    };
  };

  const description =
    'First, Select a Comparison, then Select a Program Year. The results will be displayed for each graph.' +
    ` To interact with the page, Select a Practice Area (PA) from the Appraisal Criteria or ${comparisonType.value}` +
    ' graphs to see the PA-specific results displayed underneath.' +
    ' Then, Select a Practice from the' +
    ' Practice Characterization graph to see additional practice information.';

  if (data && data.appraisalComparisonOrgUnit === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (error || industryReportError || orgReportError || searchAggregatePercentageError) {
    return <ApolloError />;
  }

  return (
    <div onClick={handleClickOutside}>
      <Page title={pageTitle} className='appraisal-comparison'>
        <Breadcrumb
          pathItems={breadcrumbPathItems}
          annotationItems={annotationItems}
          loading={appraisalComparisonLoading}>
          <DownloadAppraisalReportModal
            data={data}
            isPercentageVisible={data ? data.isPercentageVisible : null}
            showPracticeCharacterizationForUnit={
              showPracticeCharacterizationUnitForFdaUser || !isUserFDA
            }
            selectedProgramYearValue={selectedYear}
            selectedAppraisalId={selectedAppraisalId}
          />
        </Breadcrumb>

        <PageInstructions title='Appraisal Comparison Instructions' description={description} />
        <div className='appraisal-comparison-content'>
          <div className='appraisal-comparison-info-boxes'>
            <SkeletonWrapper show={appraisalComparisonLoading}>
              <div className='info-box' onClick={e => e.stopPropagation()}>
                <UnitDetailsBox data={unitDetailsBoxData} handleSelectUnit={handleSelectUnit} />
              </div>
            </SkeletonWrapper>
            <SkeletonWrapper show={appraisalComparisonLoading}>
              <div className='info-box graph-area-box' onClick={e => e.stopPropagation()}>
                <div className='graph-details-box' data-testid='practice-area-details'>
                  <h4>Practice Area: {practiceArea}</h4>
                  <div>{practiceNameAndPercentageDataUnitDetails}</div>
                </div>
              </div>
            </SkeletonWrapper>
          </div>
          <div>
            <AppraisalComparisonFilters
              data={data}
              setSelectedAppraisalId={setSelectedAppraisalId}
              selectedAppraisalId={selectedAppraisalId}
              setComparisonType={setComparisonType}
              comparisonType={comparisonType}
              loading={appraisalComparisonLoading}
            />
            <div className='appraisal-comparison-graphs'>
              <div className='appraisal-comparison-row'>
                <div className='graph-box'>
                  <AppraisalCriteriaSection
                    data={data}
                    isPercentageVisible={data ? data.isPercentageVisible : null}
                    selectedAppraisalId={selectedAppraisalId}
                    loading={appraisalComparisonLoading}
                  />
                </div>
                {!appraisalComparisonLoading && data.isPercentageVisible && (
                  <div className='graph-box second-box' data-testid='unit-comparison-section'>
                    <UnitComparisonRadar
                      orgReportData={orgReportData}
                      selectedComparison={comparisonType}
                      industryViewReportData={industryReportData}
                      unitAppraisalData={unitAppraisalData}
                      selectedProgramYearValue={selectedYear}
                      loading={orgReportLoading || industryReportLoading}
                    />
                  </div>
                )}
              </div>
              <div className='appraisal-comparison-row second-row'>
                <div className='graph-box'>
                  <PracticeCharacterizationSection
                    showPracticeCharacterizationForUnit={
                      (!isUserFDA || showPracticeCharacterizationUnitForFdaUser) &&
                      comparisonType.showUnitLine
                    }
                    orgReportData={orgReportData}
                    selectedComparisonDataKey={comparisonType.key}
                    industryViewReportData={industryReportData}
                    practiceClick={handlePracticeClick}
                    sharedWithFda={isProgramYearSharedWithFDA}
                    titleLoading={appraisalComparisonLoading}
                    loading={
                      appraisalComparisonLoading || orgReportLoading || industryReportLoading
                    }
                  />
                </div>
                {!appraisalComparisonLoading && data.isPercentageVisible && (
                  <div className='graph-box second-box' data-testid='aggregate-percentage-section'>
                    <h4>Aggregate Percentage</h4>
                    <AggregatePercentageSection
                      orgReportData={orgReportData}
                      industryViewReportData={industryReportData}
                      unitAppraisalData={unitAppraisalData}
                      selectedComparisonDataKey={comparisonType.key}
                      selectedProgramYearValue={selectedYear}
                      handleAggregatePercentageClick={handleAggregatePercentageClick}
                      loading={orgReportLoading || industryReportLoading}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Page>
    </div>
  );
};

/** @type {React.ReactNode} */
const defaultPracticeNameAndPercentageDataUnitDetails =
  'Select a practice or percentage for more information.';

export default AppraisalComparison;
